import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  fontSize,
  zIndexLayers,
  visuallyHidden,
} from '../styles';
import { Container, Button, Link } from './ui';
import CookieModal from './CookieModal';

const StyledCookieNotice = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: 100%;
  max-width: 620px;
  color: ${brandColours.primary};
  z-index: ${zIndexLayers.sixth};
  transform: translate(-50%, -50%);

  ${({ display }) => {
    if (!display) {
      return css`
        ${visuallyHidden()};
      `;
    }
  }}
`;

const StyledInner = styled.div`
  display: grid;
  gap: 20px;
  padding: 30px;
  background-color: ${brandColours.quaternary};
  border-radius: 20px;

  ${minBreakpointQuery.small`
    padding: 35px 35px 25px;
  `}

  ${minBreakpointQuery.large`
    gap: 25px;
    padding: 40px 40px 30px;
  `}
`;

const StyledContent = styled.div``;

const StyledHeading = styled.h4`
  ${fontSize(18)};

  ${minBreakpointQuery.large`
    ${fontSize(20)};
  `}
`;

const StyledText = styled.p`
  margin-top: 12px;
  color: ${brandColours.primary};
  line-height: 1.5;

  ${minBreakpointQuery.large`
    ${fontSize(18)};
  `}
`;

const StyledLink = styled(Link)`
  color: ${brandColours.tertiary};
`;

const StyledButtons = styled.div`
  ${minBreakpointQuery.small`
    display: flex;
    align-items: center;
    gap: 12px;
  `}
`;

const StyledButton = styled(Button)`
  border-radius: 25px;

  ${maxBreakpointQuery.small`
    width: 100%;
  `}

  ${({ isManage }) => {
    if (isManage) {
      return css`
        color: ${brandColours.primary};
        background-color: transparent;
        border: 1px solid ${brandColours.primary};

        ${maxBreakpointQuery.small`
          margin-top: 10px;
        `}
      `;
    } else {
      return css`
        background-color: ${brandColours.primary};
        border: 1px solid ${brandColours.primary};

        &:before {
          content: '';
          display: inline-block;
          margin-right: 15px;
          height: 14px;
          width: 8px;
          border-right: 2px solid ${standardColours.white};
          border-bottom: 2px solid ${standardColours.white};
          transform: rotate(45deg);
        }
      `;
    }
  }}
`;

const StyledContinue = styled.button`
  padding: 0;
  ${fontSize(15)};
  text-decoration: underline;

  ${minBreakpointQuery.small`
    margin-left: auto;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)};
  `}
`;

const CookieNotice = ({ setOverlayActive }) => {
  const localStorageKey = `spaseekers:cookie-decision`;
  const savedCookieLocalStorageKey = `spaseekers:cookie-preferences`;
  const [cookieDecision, setCookieDecision] = useState();
  const [isCookieModalActive, setCookieModalActive] = useState(false);

  const formRef = useRef(null);

  const cookies = [
    {
      name: 'necessary',
      heading: 'Necessary cookies',
      text: 'Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.',
      required: true,
    },
    {
      name: 'functional',
      heading: 'Functional cookies',
      text: 'Functional cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in.',
    },
    {
      name: 'analytics',
      heading: 'Analytic cookies',
      text: 'Analytic cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously.',
    },
    {
      name: 'marketing',
      heading: 'Marketing cookies',
      text: 'Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.',
    },
  ];

  const acceptCookieNotice = () => {
    localStorage.setItem(localStorageKey, 'accepted');
    setCookieDecision('accepted');
  };

  const submitDataLayer = cookieData => {
    let data = {};

    if (cookieData) {
      data = cookieData;
    } else {
      cookies.map(
        ({ name, required }) =>
          (data = {
            ...data,
            [name]: required ? 'granted' : 'denied',
          }),
      );
    }

    const eventData = {
      event: 'consent_updated',
      ...data,
    };

    // Push dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventData);
  };

  const handleSubmit = (e, acceptAll) => {
    e.preventDefault();

    const formData = new FormData(formRef.current);

    let cookieData = {};

    cookies.map(
      ({ name, required }) =>
        (cookieData = {
          ...cookieData,
          [name]: acceptAll
            ? 'granted'
            : required
              ? 'granted'
              : formData && formData.get(name)
                ? formData.get(name)
                : 'denied',
        }),
    );

    // Save cookie preferences
    localStorage.setItem(
      savedCookieLocalStorageKey,
      JSON.stringify(cookieData),
    );

    // Close modal
    setCookieModalActive(false);

    // Close overlay and set cookies
    acceptCookieNotice();
  };

  const handleContinue = () => {
    let cookieData = {};

    cookies.map(
      ({ name }) =>
        (cookieData = {
          ...cookieData,
          [name]: name === 'necessary' ? 'granted' : 'denied',
        }),
    );

    // Save cookie preferences
    localStorage.setItem(
      savedCookieLocalStorageKey,
      JSON.stringify(cookieData),
    );

    // Close modal
    setCookieModalActive(false);

    // Close overlay and set cookies
    acceptCookieNotice();
  };

  useEffect(() => {
    setCookieDecision(localStorage.getItem(localStorageKey) || false);

    if (cookieDecision === false) {
      setOverlayActive(true);
      // Fire all cookie options as denied
      submitDataLayer();
    } else {
      // Fire the saved cookie preferences
      const cookieData = localStorage.getItem(savedCookieLocalStorageKey);

      cookieDecision && submitDataLayer(JSON.parse(cookieData));

      setOverlayActive(false);
    }
  }, [localStorageKey, cookieDecision, setOverlayActive]);

  return (
    cookieDecision === false && (
      <>
        <StyledCookieNotice display={!isCookieModalActive}>
          <Container wide={true}>
            <StyledInner>
              <StyledContent>
                <StyledHeading>Can we use optional cookies?</StyledHeading>
                <StyledText>
                  We use cookies to make your experience better. By using
                  spaseekers.com, you accept our{' '}
                  <StyledLink to="/privacy-policy/">cookie notice</StyledLink>{' '}
                  terms.
                </StyledText>
              </StyledContent>
              <StyledButtons>
                <StyledButton alt={true} onClick={e => handleSubmit(e, true)}>
                  I accept
                </StyledButton>
                <StyledButton
                  isManage={true}
                  onClick={() => setCookieModalActive(true)}
                >
                  Manage
                </StyledButton>
              </StyledButtons>
              <StyledContinue onClick={() => handleContinue()}>
                Continue without accepting
              </StyledContinue>
            </StyledInner>
          </Container>
        </StyledCookieNotice>
        <CookieModal
          formRef={formRef}
          cookies={cookies}
          handleSubmit={handleSubmit}
          isCookieModalActive={isCookieModalActive}
        />
      </>
    )
  );
};

export default CookieNotice;
